/** Gap between the sender chip and its tooltip, in px. */
export const SENDER_INFO_TOOLTIP_GAP = 8;

/** Breathing room kept between the tooltip and the viewport's left/right edge, in px. */
export const SENDER_INFO_TOOLTIP_PADDING = 16;

/** The parts of a `DOMRect` the placement actually reads. */
export interface TooltipAnchorRect {
	top: number;
	bottom: number;
	left: number;
	width: number;
	height: number;
}

export interface TooltipViewport {
	width: number;
	height: number;
}

export interface TooltipPosition {
	top: number;
	left: number;
}

/**
 * Places the sender tooltip as a viewport-fixed box next to its trigger.
 *
 * Coordinates are viewport ones because the tooltip is `position: fixed` (web rule §11): adding
 * scroll offsets to a body-portalled absolute box grows the document and turns the whole app into a
 * scrollable page, which is PREC-4114.
 *
 * Each direction flips to the opposite side when its preferred side would leave the viewport, and
 * the result is then clamped horizontally so a tooltip opened near an edge stays reachable.
 *
 * Extracted from the component's layout effect unchanged (PREC-4116): the arithmetic is the whole
 * behaviour here and it is only reachable through a real `getBoundingClientRect`, so in the
 * component it was untestable in jsdom — where every rect is zero — and the flip branches were
 * never exercised.
 */
export const resolveSenderInfoTooltipPosition = ({
	direction,
	trigger,
	tooltip,
	viewport,
}: {
	direction: string;
	trigger: TooltipAnchorRect;
	tooltip: Pick<TooltipAnchorRect, "width" | "height">;
	viewport: TooltipViewport;
}): TooltipPosition => {
	const gap = SENDER_INFO_TOOLTIP_GAP;
	const padding = SENDER_INFO_TOOLTIP_PADDING;

	let top = 0;
	let left = trigger.left + trigger.width / 2 - tooltip.width / 2;

	if (direction === "top") {
		top = trigger.top - tooltip.height - gap;

		// Not enough room above — drop below the trigger instead.
		if (trigger.top - tooltip.height - gap < 0) {
			top = trigger.bottom + gap;
		}
	} else if (direction === "bottom") {
		top = trigger.bottom + gap;

		// Not enough room below — lift above the trigger instead.
		if (trigger.bottom + tooltip.height + gap > viewport.height) {
			top = trigger.top - tooltip.height - gap;
		}
	} else if (direction === "top-right") {
		top = trigger.top - tooltip.height - gap + 20;
		left = 180;

		// There is room below after all, so prefer it and start from the trigger's own left edge.
		if (trigger.bottom + tooltip.height + gap < viewport.height) {
			left = 0;
			top = trigger.bottom + gap;
		}
	}

	if (left < padding) {
		left = padding;
	} else if (left + tooltip.width > viewport.width - padding) {
		left = viewport.width - tooltip.width - padding;
	}

	// A "bottom" tooltip that flipped up can still land above the viewport on a short trigger;
	// park it just inside the top edge rather than off-screen.
	if (top < 0 && direction === "bottom") {
		top = 20;
	}

	return { top, left };
};
